import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { TEXT_SIZES, type TextSize } from '../lib/textSize'
import { DiscardDialog, useLayer } from './layers'
import styles from './Appearance.module.css'

const SPOKEN: Record<TextSize, string> = {
  default: 'Default text size',
  large: 'Large text, 109%',
  xlarge: 'Extra large text, 120%',
}

// The three round A buttons at the foot of the rail (h 1536-1539): ascending
// size, the active one filled accent. Present on every page, because the
// people who need it need it everywhere.
export function TextSizeButtons({ size, onChange }: { size: TextSize; onChange: (s: TextSize) => void }) {
  return (
    <div className={styles.rail}>
      <span className={styles.railLabel} id="dp-text-size-label">
        Text size
      </span>
      <div className={styles.aRow} role="radiogroup" aria-labelledby="dp-text-size-label">
        {TEXT_SIZES.map((s, i) => {
          const on = s.key === size
          return (
            <button
              key={s.key}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={SPOKEN[s.key]}
              title={SPOKEN[s.key]}
              className={`${styles.a} ${styles['a' + i]} ${on ? styles.aOn : ''}`}
              onClick={() => onChange(s.key)}
            >
              A
            </button>
          )
        })}
      </div>
    </div>
  )
}

// What the old settings dialog keeps (h 1253-1256): Appearance and Account,
// in a popup you dismiss. Users, roles and system settings are pages in the
// rail's Configuration section, not entries here.
export function AccountPopup({
  anchor,
  name,
  email,
  workspace,
  size,
  onSize,
  onSignOut,
  onClose,
}: {
  anchor: React.RefObject<HTMLElement>
  name: string
  email: string
  workspace: string
  size: TextSize
  onSize: (s: TextSize) => void
  onSignOut: () => void
  onClose: () => void
}) {
  const pop = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<{ left: number; bottom: number } | null>(null)
  useLayer(true, onClose, { el: pop })
  // Sign out asks first: a stray click on it should not end the session.
  const [asking, setAsking] = useState(false)
  const askingRef = useRef(false)
  askingRef.current = asking

  // A press anywhere off it closes it (as the task centre does), except on
  // the Sign out question, which sits over it.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (askingRef.current || pop.current?.contains(t) || anchor.current?.contains(t)) return
      onClose()
    }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [onClose, anchor])

  useEffect(() => {
    const r = anchor.current?.getBoundingClientRect()
    if (r) setAt({ left: Math.max(8, r.left), bottom: Math.max(8, window.innerHeight - r.top + 8) })
    pop.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus()
  }, [anchor])

  return createPortal(
    <div
      ref={pop}
      className={styles.pop}
      style={at ? { left: at.left, bottom: at.bottom } : undefined}
      role="dialog"
      aria-label="Appearance and account"
      data-testid="account-popup"
    >
      <section className={styles.section} aria-labelledby="dp-appearance-h">
        <h2 id="dp-appearance-h" className={styles.head}>
          Appearance
        </h2>
        <div className={styles.fieldLabel} id="dp-text-size-pop">
          Text size
        </div>
        <div className={styles.seg} role="radiogroup" aria-labelledby="dp-text-size-pop">
          {TEXT_SIZES.map((s) => {
            const on = s.key === size
            return (
              <button key={s.key} type="button" role="radio" aria-checked={on} className={`${styles.segBtn} ${on ? styles.segOn : ''}`} onClick={() => onSize(s.key)}>
                {s.label}
              </button>
            )
          })}
        </div>
        <p className={styles.note}>Saved to your account, so it follows you to any computer you sign in on. The A buttons at the foot of the menu set it too.</p>
      </section>
      <section className={styles.section} aria-labelledby="dp-account-h">
        <h2 id="dp-account-h" className={styles.head}>
          Account
        </h2>
        <div className={styles.who}>
          <span className={styles.whoLabel}>Signed in as</span>
          <strong className={styles.whoName}>{name}</strong>
          {email && email !== name && <span className={styles.whoEmail}>{email}</span>}
        </div>
        <div className={styles.who}>
          <span className={styles.whoLabel}>Workspace</span>
          <span className={styles.whoName}>{workspace}</span>
        </div>
        <button type="button" className={styles.signOut} onClick={() => setAsking(true)}>
          Sign out
        </button>
      </section>
      {asking && (
        <DiscardDialog
          question="Sign out of CONTROL?"
          keepLabel="Cancel"
          discardLabel="Sign out"
          onKeep={() => setAsking(false)}
          onDiscard={onSignOut}
        />
      )}
    </div>,
    document.body
  )
}
